import { Component, ViewChild } from '@angular/core';
import { WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import {
  WizBasePopUp,
  WizDatasource,
  WizDropdown,
  WizLabel,
  WizRoot,
  WizEventManager, WizEvent
} from '@wizeoo/akm-component-lib';
import { Baseline, FormulaEngineInstance } from '@wizeoo/akm-message-lib';
import { AlkEvent, FeiEngine, FeiVO } from '@wizeoo/akm-common-lib';
import { ALK_EVENT_TYPE, WIDGET_ACTION_ORIGIN, WIDGET_STATE } from '@wizeoo/akm-common-lib';
import { Model } from 'src/app/model/model';
import { BaselineEngine, VisionVO, WizConstants } from '@wizeoo/akm-compute-lib';
import { VisionStepVO } from '@wizeoo/akm-common-lib';
import { ToolPivot } from '@wizeoo/akm-common-lib';

/**
 * renderer du pannel de droite de la vue de paramétrage des languages
 */
@Component({
  selector: 'incexc-sample-pop',
  templateUrl: './incexc-sample.pop.html',
})
export class IncExcSamplePop extends WizBasePopUp {
  @ViewChild('pivotSample') pivotSample: ToolPivot; // pivot de simulation
  @ViewChild('ddSteps', { read: WizDropdown }) ddSteps: WizDropdown;
  @ViewChild('lblResult', { read: WizLabel }) lblResult: WizLabel;
  public data: any;

  public static requestWidth: string = '800px'; // pour override de prise de taille sur construction. Sera accessible depuis la factory

  public vision: VisionVO = null;
  public currentLevel: number = 1;
  public fullLinestMap: Map<number, Map<string, Baseline>> = null; // les lignes contruites, ventilée par level / [key] = [baseline]
  public fakeFeiMap: Map<number, FeiVO> = null;
  public sizeMap: Map<number, number> = null;

  /** Data source des clés */
  public stepDS: WizDatasource<VisionStepVO> = new WizDatasource<VisionStepVO>([])
    .setSortFields([{ property: 'level', way: 'asc', numeric: true }])
    .setItemCodeField('level')
    .build();

  public show(): void {
    super.show();
    // écoute de la sélection d'une langue dans la vue
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.INC_EXC_UPDATE_SAMPLE, this.updateData);
    WizEventManager.addListener(this, this.ddSteps, WIZ_EVENT_TYPE.SELECTION_CHANGED, this.selectStep);
    // mise jour du context de la vue
    this.feed();
  }

  public get model(): Model {
    return Model.getInstance();
  }

  /**
   * méthode appelée à la fermeture de la pop
   */
  public onHide(): void {
    super.onHide();
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.INC_EXC_UPDATE_SAMPLE);
    WizEventManager.removeListener(this, this.ddSteps, WIZ_EVENT_TYPE.SELECTION_CHANGED);
  }

  /**
   * méthode de mise à jour des données de la vue (e.data = IncExcKeyVO)
   */
  private updateData(e: AlkEvent): void {
    // console.log('samplePop update', e.data);
    this.feed();
  }

  private selectStep(e: WizEvent): void {
    this.currentLevel = this.ddSteps.selectedItem ? (this.ddSteps.selectedItem as VisionStepVO).level : 1;
    this.pivotSample.context = null;
    this.feedPivot();
  }

  private feedPivot(): void {
    if (!this.fakeFeiMap.has(this.currentLevel)) {
      this.buildFei(this.currentLevel);
    }

    const fei: FeiVO = this.fakeFeiMap.get(this.currentLevel);
   
    this.pivotSample.state = WIDGET_STATE.READY;
    this.pivotSample.vision = fei.vision;
    this.pivotSample.visionStep = fei.visionStep;
    this.pivotSample.fei = fei; // on byPass le setDatasource
    let context = this.pivotSample.generateNewContext(this.pivotSample.widgetKind);
    this.pivotSample.feedContextVital(this.pivotSample.widgetKind, context);
    this.pivotSample.context = context; // création d'un contexte minimal
    
    this.pivotSample.alterContent(WIDGET_ACTION_ORIGIN.READY); // on déclenche le cycle de vie

    this.lblResult.wizLabel = this.sizeMap.get(this.currentLevel) + ' element(s)';
  }

  /** construction d'un FEI à la demande, et alimentation de la map  */
  private buildFei(level: number): void {

    const fei: FormulaEngineInstance = new FormulaEngineInstance();
    fei.baselines = {};
    fei.vision = this.vision.source;
    fei.visionStep = BaselineEngine.getVisionStepByLevel(this.vision, level);
    // on crée une entrée en Fake (scenar @@FAKE)
    let fakeFei: FeiVO = new FeiVO(fei, this.model, this.vision, fei.visionStep, WizConstants.SCEN_FAKE_KEY_CODE);
    fakeFei.model = this.model;
    this.model.feiEngine.buildToolFEIKeys(fakeFei, FeiEngine.TOOL_MODE_INC, [...this.fullLinestMap.get(fei.visionStep.level).values()]);
    this.fakeFeiMap.set(level, fakeFei);
  }

  private feed(): void {
    this.vision = this.model.currentVision;
    if (this.vision) {
      this.fakeFeiMap = new Map<number, FeiVO>(); // on construit les FEIs
      this.sizeMap = new Map<number, number>();

      // console.time('computeSample');
      this.fullLinestMap = this.model.incExcSampleEngine.buildFakeBaselines(this.vision, this.model.incExcKeyCollection);
      // console.timeEnd('computeSample');

      Array.from(this.fullLinestMap.keys()).forEach((lvl: number) => {
        this.sizeMap.set(lvl, this.fullLinestMap.get(lvl).size);
      });

      this.buildFei(this.currentLevel);

      let stepVO: VisionStepVO[] = Object.values(this.vision.visionSteps).map(item => new VisionStepVO(item));
      let startVO: VisionStepVO = stepVO.filter(item => item.level === 1)[0];

      this.stepDS.setSource(stepVO);
      this.stepDS.select([startVO]);

      this.feedPivot();
    }
  }
}
